/**
 * @file  src\helpers\nice2HaveMatch.js
 * @brief  Pure helpers for matching provider-returned property features against
 *         the inventory nice-to-have catalog, and extracting Google address
 *         components for the property-search lookup.
 */

const NEGATION_WORDS = new Set(["no", "not", "without", "non"]);

export const normalizeFeatureText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// A feature phrase like "No Garage" or "Without Pool" describes the absence
// of the thing, not its presence - it must never be treated as a match.
export const isNegatedFeature = (feature) => {
  const words = String(feature || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return words.length > 0 && NEGATION_WORDS.has(words[0]);
};

export const matchNiceToHaves = (features, niceToHaveResult) => {
  if (!Array.isArray(features) || !features.length || !niceToHaveResult)
    return [];
  const normalizedFeatures = features
    .filter((feature) => !isNegatedFeature(feature))
    .map(normalizeFeatureText)
    .filter(Boolean);
  if (!normalizedFeatures.length) return [];
  const matched = [];
  const seen = new Set();
  Object.entries(niceToHaveResult).forEach(([tab, options]) => {
    if (!Array.isArray(options)) return;
    options.forEach((option) => {
      const optionText = normalizeFeatureText(option.Text);
      const key = tab + ":" + option.Id;
      if (!optionText || seen.has(key)) return;
      const isMatch = normalizedFeatures.some(
        (feature) =>
          feature === optionText ||
          (feature.includes(optionText) && optionText.length >= 5) ||
          (optionText.startsWith(feature) && feature.length >= 5),
      );
      if (isMatch) {
        seen.add(key);
        matched.push({
          value: option.Id,
          label: option.Text,
          id: option.Id,
          tab,
          Icon: option.Icon,
          Img: option.Img,
          Webp_Img: option.Webp_Img,
        });
      }
    });
  });
  return matched.slice(0, 16).map((item, index) => ({ ...item, order: index + 1 }));
};

export const getAddressComponent = (components, type) => {
  const match = (components || []).find((item) => (item.types || [])[0] === type);
  return match ? match.short_name : null;
};

export const extractAddressComponents = (address) => {
  const components = (address && address.address_components) || [];
  const streetNumber = getAddressComponent(components, "street_number");
  const route = getAddressComponent(components, "route");
  return {
    street: [streetNumber, route].filter(Boolean).join(" ") || null,
    city: getAddressComponent(components, "locality"),
    state: getAddressComponent(components, "administrative_area_level_1"),
    zipcode: getAddressComponent(components, "postal_code"),
  };
};
